import React, { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import { roleOptionsFor, normalizeNewRole } from "./crewRoles";

/**
 * Popover content used by CrewCell and FloatingCrewCell.
 *
 *  • Lists project-configured roles (always with "lead" first)
 *  • "+ NEW ROLE" inline editor — adds to project.crew_roles, then picks it
 *
 * Props
 *   crewMember   — { id, name } for the popover header
 *   isNA         — true when crew is unavailable on this date (shows ⚠)
 *   crewRoles    — array of project roles (from project.crew_roles)
 *   onPick       — (roleKey: string) => Promise<void> — assigns crew at this role
 *   onAddRole    — (newRole: string) => Promise<void> — extends crew_roles list
 */
export default function RolePicker({ crewMember, isNA, crewRoles, onPick, onAddRole }) {
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const options = roleOptionsFor(crewRoles);

  const commitNewRole = async () => {
    const normalized = normalizeNewRole(draft, crewRoles);
    if (!normalized) {
      toast.warning(
        draft.trim() === ""
          ? "Role name required"
          : draft.trim().toLowerCase() === "lead" || (crewRoles || []).includes(draft.trim().toLowerCase())
            ? "That role already exists"
            : "Use letters, numbers, spaces, _ or -"
      );
      return;
    }
    if (!onAddRole) return;
    setBusy(true);
    try {
      await onAddRole(normalized);
      // Immediately pick the new role for this crew member.
      await onPick(normalized);
      setDraft("");
      setAddOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-bg-elevated border border-line-strong shadow-[0_8px_24px_rgba(0,0,0,0.7)] min-w-[160px]" data-testid="crew-role-picker">
      <div className="px-2 py-1 mono text-2xs uppercase tracking-widest text-ink-mute border-b border-line">
        ASSIGN {crewMember?.name || "—"}
        {isNA && <span className="text-amber ml-1">⚠ NA</span>}
      </div>
      {options.map((roleKey) => (
        <button
          key={roleKey}
          onClick={() => onPick(roleKey)}
          className="w-full text-left px-2 py-1 mono text-2xs uppercase tracking-widest text-ink hover:bg-bg-highlight flex items-center gap-1.5"
          data-testid={`crew-role-${roleKey}`}
        >
          {roleKey === "lead" ? (
            <span className="text-amber">LEAD</span>
          ) : (
            <>
              <span aria-hidden style={{ fontSize: "0.9em" }}>●</span>
              <span>{roleKey}</span>
            </>
          )}
        </button>
      ))}
      <div className="border-t border-line">
        {addOpen ? (
          <div className="flex items-center gap-1 px-2 py-1">
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitNewRole();
                if (e.key === "Escape") {
                  setAddOpen(false);
                  setDraft("");
                }
              }}
              placeholder="role name"
              maxLength={24}
              className="flex-1 mono text-2xs uppercase tracking-widest bg-bg-base border border-line px-1.5 py-0.5 text-ink placeholder:text-ink-mute focus:outline-none focus:border-amber"
              data-testid="crew-role-new-input"
            />
            <button
              onClick={commitNewRole}
              disabled={busy}
              className="mono text-2xs uppercase tracking-widest text-amber px-1.5 py-0.5 hover:bg-amber-glow"
              data-testid="crew-role-new-confirm"
            >
              OK
            </button>
          </div>
        ) : (
          <button
            onClick={() => setAddOpen(true)}
            className="w-full text-left px-2 py-1 mono text-2xs uppercase tracking-widest text-ink-mute hover:text-amber hover:bg-bg-highlight flex items-center gap-1.5"
            data-testid="crew-role-new"
            disabled={!onAddRole}
            title={onAddRole ? "Add a new role to this project" : "Roles are read-only here"}
          >
            <Plus className="w-2.5 h-2.5" /> NEW ROLE
          </button>
        )}
      </div>
    </div>
  );
}
